import {
  Box,
  Button,
  Card,
  createStyles,
  Grid,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconShoppingCartPlus } from '@tabler/icons'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import ProductAlert from '@/components/commons/ProductAlert'
import BuyButton from '@/containers/showcases/BuyButton'
import {
  Product as ProductType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'

import PriceLabel from './PriceLabel'

interface Props {
  site?: SiteType
  store?: StoreType
  showcase?: ShowcaseType
  product: ProductType
  isRecommended?: boolean
}

const useStyles = createStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between'
  },
  productImageWrapper: {
    position: 'relative',
    width: '100%',
    height: 200,
    backgroundColor: theme.colors[theme.primaryColor][0],
    [theme.fn.smallerThan('xs')]: { height: 'calc(50vw - 45px)' }
  },
  sellerLogo: {
    borderRadius: '50%',
    left: 10,
    height: 30,
    position: 'absolute',
    top: 10,
    width: 30,
    zIndex: 1
  },
  actionButton: { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
}))

export default function ProductCard({
  site,
  store,
  showcase,
  product,
  isRecommended = false
}: Props) {
  // Hooks
  const router = useRouter()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // Constants
  const {
    channel: channelSlug,
    collection: collectionSlug,
    store: storeSlug,
    showcase: showcaseSlug
  } = router.query || {}
  const recommendedUrl = `/${channelSlug}/${storeSlug}/${showcaseSlug}/${product.slug}${
    collectionSlug ? `?collection=${collectionSlug}` : ''
  }`
  const productImage = product.primaryImage
    ? product.primaryImage.file
    : '/images/placeholders/store.png'
  const productHref = isRecommended
    ? recommendedUrl
    : `${router.asPath.split('?')[0]}/${product.slug}${
        collectionSlug ? `?collection=${collectionSlug}` : ''
      }`

  return (
    <Card key={product.uid} withBorder p={0} className={classes.card}>
      <Card.Section>
        <UnstyledButton component={Link} href={productHref}>
          <Box className={classes.productImageWrapper}>
            {!product.isKitBundler && (
              <Tooltip
                label={`Vendido por ${product?.seller?.displayName}`}
                openDelay={0}
                multiline
                position="right">
                <Box className={classes.sellerLogo}>
                  <Image
                    src={product?.seller?.logo || ''}
                    alt={`Vendido por ${product?.seller?.displayName}`}
                    quality={60}
                    width={30}
                    height={30}
                  />
                </Box>
              </Tooltip>
            )}
            <Image src={productImage} alt="Loja" fill style={{ objectFit: 'cover' }} quality={60} />
          </Box>
        </UnstyledButton>
        <Grid p="sm" justify="center" align="center" grow>
          <Grid.Col span={9} xs={12}>
            <Title mb="xs" order={isXs ? 4 : 5} sx={{ lineHeight: 1.2 }}>
              {product.title}
            </Title>
            <Text size="sm" color="dimmed" lineClamp={3} sx={{ lineHeight: 1.2 }}>
              {product.subtitle}
            </Text>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Card.Section>
        <Box p="sm">
          <PriceLabel product={product} />
        </Box>
        {!!product.hasStock && (
          <>
            {product.quickAddToBasket ? (
              <BuyButton
                product={product}
                siteData={site}
                store={store}
                showcase={showcase}
                addToCart
                addToCartLabel="Adicionar"
                addToCartProps={{
                  leftIcon: <IconShoppingCartPlus size={16} />,
                  className: classes.actionButton
                }}
                buyNow={false}
                continueBuying={false}
              />
            ) : (
              <Button
                component={Link}
                variant="light"
                fullWidth
                href={productHref}
                className={classes.actionButton}>
                Detalhes
              </Button>
            )}
          </>
        )}
        {!product.hasStock && (
          <ProductAlert productSlug={product.slug} className={classes.actionButton} />
        )}
      </Card.Section>
    </Card>
  )
}
